import { getBuyUrlNameByLocalLanguage, getRegionLanguageInRouter } from '@source/utils/utils';

import { NextRouter } from 'next/router';
import { Option } from '@source/interface/common';
/* eslint-disable no-plusplus */
import { SettingProps } from '@design-system/components/FiltersV1/FilterProps';
import { TRegionKey } from '@source/interface';
import { URL_CONFIG } from '@source/constants/urlConfig';
import get from 'lodash/get';
import { getFilterSelectedOptionsV2 } from '@design-system/utils/filters';
import isEmpty from 'lodash/isEmpty';
import { isNil } from 'lodash';
import { mapFilterNameFromQS } from './components/Filters/configs/constants';
import omit from 'lodash/omit';
import qs from 'qs';
import set from 'lodash/set';

export const getUniqSearchHistories = (histories: string[], value: string) =>
  histories.find((item) => value?.toLowerCase() === item?.toLowerCase()) ? histories : [value, ...histories];

export const removeDuplicateSelectedOptions = (
  newOptions: Option<string>[],
  currentOptions: (Option<string> | string)[],
) =>
  newOptions.filter((newOption) => {
    const existingValue = currentOptions.find((currentOption) => {
      if (typeof currentOption === 'string') {
        return currentOption === newOption.value;
      }

      return currentOption.value === newOption.value;
    });
    return !existingValue;
  });

//  if options are provided, get value from options and if no label is found, remove from suggestions
export const convertToLabelValuePairArray = (options: string[], optionsKeyValuesArray: Option<string>[] = []) => {
  const hasOptions = optionsKeyValuesArray.length > 0;

  const getLabel = (value: string) => {
    const label = optionsKeyValuesArray.find((option) => option.value === value)?.label;
    if (hasOptions && !label) {
      return '';
    }
    return label || value;
  };

  return options.map((value) => ({ label: getLabel(value), value })).filter((option) => option.label);
};

export const isSearchable = (query: string) => query.length >= 3;

export const isSingleBrandOrModel = (data: string | undefined) => (data ? data.split(',')?.length === 1 : false);

export const areModelsTheSameBrandFromQS = (brand: string | undefined, models: string | undefined) => {
  if (brand) {
    if (models) {
      const diffBrand = models?.split(',').find((model) => !model.toLowerCase()?.includes(brand?.toLowerCase()));
      return !diffBrand;
    }
    return true;
  }
  return false;
};

export const removeUnnescessaryModelsInQS = (brands: string | undefined, models: string | undefined) => {
  if (!brands) return models;
  const brandNames = brands.toLowerCase().split(',');
  if (models) {
    const modelNames = models.toLowerCase().split(',');
    let result: Array<string> = [];
    result = modelNames.filter((model) => !brandNames.includes(model.split(' ')?.[0]));
    return result.join(',');
  }
  return undefined;
};

export const redirectBaseOnUrl = (
  router: NextRouter,
  countryCode: TRegionKey,
  filterSettings: SettingProps[],
  currentQuery: string,
  otherQSObj?: { [key: string]: string | number },
  isFilterComponent?: boolean,
  isBuyNewPage?: boolean,
): string => {
  const selectedFilters = getFilterSelectedOptionsV2(filterSettings, countryCode);
  let configUrl = '';
  if (selectedFilters) {
    const routerQuery = qs.parse(qs.stringify(router.query));
    if (routerQuery.query) routerQuery.query = encodeURIComponent(routerQuery.query as string);

    // keep other query params that are not filter params
    const allUrlFilterName = Object.keys(mapFilterNameFromQS(countryCode));
    const newRouterQuery = Object.keys(routerQuery)
      .filter((item) => ![...allUrlFilterName, 'brand', 'model'].includes(item))
      .reduce((obj, key: string) => {
        set(obj, key, routerQuery[key]);
        return obj;
      }, {});

    const { region, language } = getRegionLanguageInRouter(router, countryCode as TRegionKey);
    let qsObject = { ...newRouterQuery, ...selectedFilters, ...(otherQSObj || {}) };
    if (isEmpty(currentQuery)) qsObject = omit(qsObject, 'query');
    qsObject = omit(qsObject, 'lang');

    const selectedBrands = get(selectedFilters, 'brands');
    const selectedModels = get(selectedFilters, 'models');
    const selectedMakes = decodeURIComponent(get(selectedFilters, 'makes') || ('' as string));

    const buyUrlName = isBuyNewPage ? 'buy-new' : getBuyUrlNameByLocalLanguage(countryCode, language);

    // when only 1 brand is selected
    if (
      selectedBrands &&
      isSingleBrandOrModel(selectedBrands) &&
      areModelsTheSameBrandFromQS(selectedBrands, selectedModels)
    ) {
      qsObject = omit(qsObject, 'brands');
      qsObject = omit(qsObject, 'models');
      qsObject = omit(qsObject, 'makes');
      configUrl = URL_CONFIG({
        region: region as TRegionKey,
        lang: language,
        query: qs.stringify(qsObject, { arrayFormat: 'comma', encode: false }),
        path: `${buyUrlName}/${(selectedBrands as string).toLowerCase()}`,
      }).formatted;
      // router.push(configUrl, configUrl, { shallow: true });
      return configUrl;
    }

    // when only 1 model is selected
    if (selectedModels && isSingleBrandOrModel(selectedModels)) {
      qsObject = omit(qsObject, 'models');
      qsObject = omit(qsObject, 'makes');

      // split make_model into make and model by selected make
      const regex = new RegExp(`^(${selectedMakes})\\s+(.*)$`, 'i');
      const match = (selectedModels as string).match(regex);
      const model = match ? match[2] : '';
      const modelName = model.split(' ').join('-');

      configUrl = URL_CONFIG({
        region: region as TRegionKey,
        lang: language,
        query: qs.stringify(qsObject, { arrayFormat: 'comma', encode: false }),
        path: `${buyUrlName}/${selectedMakes.toLowerCase()}/${modelName.toLowerCase()}`,
      }).formatted;

      return configUrl;
    }

    // when there is no filter selected
    if (isEmpty(selectedFilters)) {
      configUrl =
        isFilterComponent || !isEmpty(qsObject)
          ? URL_CONFIG({
              region: region as TRegionKey,
              lang: language,
              query: qs.stringify(qsObject, { arrayFormat: 'comma', encode: false }),
            })[isBuyNewPage ? 'buyNew' : 'home']
          : '';

      return configUrl;
    }

    // remove model names in url query under the selected brands
    if (selectedModels) {
      const finalModelsInQS = removeUnnescessaryModelsInQS(selectedBrands, selectedModels);
      set(qsObject, 'models', finalModelsInQS || undefined);
    }

    // remove makes in url query, make is only used for detect model in make_model
    qsObject = omit(qsObject, 'makes');

    configUrl = URL_CONFIG({
      region: region as TRegionKey,
      lang: language,
      query: qs.stringify(qsObject, { arrayFormat: 'comma', encode: false }),
      path: buyUrlName,
    }).formatted;

    return configUrl;
  }

  return configUrl;
};

/**
 * Merges two arrays by alternating chunks of elements from each array.
 * Takes `numFromArr1` elements from `arr1` and `numFromArr2` elements from `arr2` in each iteration.
 * Continues until all elements from both arrays are added to the result.
 *
 * @param arr1 - First array to merge from.
 * @param arr2 - Second array to merge from.
 * @param numFromArr1 - Number of elements to take from arr1 per iteration.
 * @param numFromArr2 - Number of elements to take from arr2 per iteration.
 *
 * @returns A new array with merged elements.
 */
export const customMergeArrays = (arr1: any[], arr2: any[], numFromArr1: number, numFromArr2: number) => {
  const result = [];
  let i = 0;
  let j = 0;

  while (i < arr1.length || j < arr2.length) {
    // Take elements from arr1
    for (let k = 0; k < numFromArr1 && i < arr1.length; k++) {
      result.push(arr1[i++]);
    }

    // Take elements from arr2
    for (let k = 0; k < numFromArr2 && j < arr2.length; k++) {
      result.push(arr2[j++]);
    }
  }

  return result;
};

/**
 * Inserts a dynamically generated item at every `nth` position within groups of `groupSize` elements.
 * The item to be inserted is provided by the `getItem` function.
 *
 * @param {any[]} arr - The input array.
 * @param {number} groupSize - The total size of each group after insertion.
 * @param {number} nth - The 1-based position within each group to insert the item.
 * @param {(groupIndex: number, index: number) => any} getItem - Function to generate the item to insert.
 *
 * @returns {any[]} A new array with items inserted at the specified positions.
 */
export const insertItemEveryNth = (
  arr: any[],
  groupSize: number,
  nth: number,
  getItem: (groupIndex: number, index: number) => any,
) => {
  if (groupSize < nth) {
    return [...arr, getItem(0, 0)];
  }
  const result = [];
  const numbersPerGroup = groupSize - 1;
  let groupIndex = 0;

  for (let i = 0; i < arr.length; i += numbersPerGroup) {
    const group = arr.slice(i, i + numbersPerGroup);

    if (group.length === numbersPerGroup) {
      const indexToInsert = nth - 1;
      const itemToInsert = getItem(groupIndex, i);

      // only insert if the item is not null
      if (!isNil(itemToInsert)) {
        group.splice(indexToInsert, 0, itemToInsert);
      }
    }

    result.push(...group);
    groupIndex++;
  }

  return result;
};

/**
 * Filters the current URL and navigates to a new URL if it is different from the current one.
 * @param {NextRouter} router - The Next.js router object.
 * @param {string | undefined | null} url - The URL to navigate to.
 */
export const filterRedirect = (router: NextRouter, url: string | undefined | null) => {
  if (!url) return;
  const { asPath, locale } = router;

  // Handle edge case locale and region the same
  if (locale && url.startsWith(`/${locale}`)) {
    let currentUrl = asPath;

    if (locale) {
      currentUrl = `/${router.locale}${currentUrl}`;
    }

    if (
      url === currentUrl ||
      decodeURIComponent(url) === currentUrl ||
      url === decodeURIComponent(currentUrl) ||
      decodeURIComponent(url) === decodeURIComponent(currentUrl)
    )
      return;

    router.push(url, url, { shallow: true, locale });
  } else {
    // Back to default case
    router.push(url, url, { shallow: true });
  }
};

export const getInitMetaData = (brandModel: string, companyName: string) => {
  return {
    my: {
      en: {
        title: `Used ${brandModel} Cars for Sale in Malaysia | ${companyName}`,
        metaTitle: `Used ${brandModel} Cars for Sale in Malaysia | ${companyName}`,
        description: `Buy certified, high-quality used ${brandModel} cars in Malaysia with a money-back guarantee!`,
      },
      ms: {
        title: `Harga kereta terpakai ${brandModel} di Malaysia | Carro`,
        metaTitle: `Harga kereta terpakai ${brandModel} di Malaysia | Carro`,
        description: `Beli kereta terpakai ${brandModel} kualiti tinggi di Malaysia dengan jaminan wang dikembalikan`,
      },
      'zh-my': {
        title: `购买在马来西亚出售的二手${brandModel}汽车 | ${companyName}`,
        metaTitle: `购买在马来西亚出售的二手${brandModel}汽车 | ${companyName}`,
        description: `在马来西亚购买经过认证优质的二手${brandModel}汽车。退款保证!`,
      },
    },
    sg: {
      en: {
        title: `Used ${brandModel} Cars for Sale in Singapore | ${companyName}`,
        metaTitle: `Used ${brandModel} Cars for Sale in Singapore | ${companyName}`,
        description: `Buy certified, high-quality used ${brandModel} cars in Singapore with a money-back guarantee!`,
      },
      'zh-sg': {
        title: `购买在新加坡出售的二手${brandModel}汽车 | ${companyName}`,
        metaTitle: `购买在新加坡出售的二手${brandModel}汽车 | ${companyName}`,
        description: `在新加坡购买经过认证优质的二手${brandModel}汽车。退款保证!`,
      },
      ms: {
        title: `${brandModel} terpakai untuk dijual di Singapore | ${companyName}`,
        metaTitle: `${brandModel} terpakai untuk dijual di Singapore | ${companyName}`,
        description: `Beli kereta ${brandModel} terpakai yang disahkan dan berkualiti tinggi di Singapore dengan jaminan wang balik!`,
      },
    },
    th: {
      en: {
        title: `Used ${brandModel} Cars for Sale in Thailand | ${companyName}`,
        metaTitle: `Used ${brandModel} Cars for Sale in Thailand | ${companyName}`,
        description: `Buy certified, high-quality used ${brandModel} cars in Thailand with a money-back guarantee!`,
      },
      th: {
        title: `รถ ${brandModel} มือสองที่วางขายในประเทศไทย | คาร์โร ประเทศไทย`,
        metaTitle: `รถ ${brandModel} มือสองที่วางขายในประเทศไทย | คาร์โร ประเทศไทย`,
        description: `ซื้อรถ ${brandModel} มือสอง คุณภาพดี การันตียินดีคืนเงิน`,
      },
    },
    id: {
      en: {
        title: `Used ${brandModel} Cars for Sale in Indonesia | ${companyName}`,
        metaTitle: `Used ${brandModel} Cars for Sale in Indonesia | ${companyName}`,
        description: `Buy certified, high-quality used ${brandModel} cars in Indonesia with a money-back guarantee!`,
      },
      id: {
        title: `Harga Mobil Bekas ${brandModel} di Indonesia | Carro Indonesia`,
        metaTitle: `Harga Mobil Bekas ${brandModel} di Indonesia | Carro Indonesia`,
        description: `Beli ${brandModel} bekas dan berkualitas dengan jaminan uang kembali`,
      },
    },
    jp: {
      en: {
        title: `Used ${brandModel} Cars for Sale in Japan | ${companyName}`,
        metaTitle: `Used ${brandModel} Cars for Sale in Japan | ${companyName}`,
        description: `Buy certified, high-quality used ${brandModel} cars in Japan with a money-back guarantee!`,
      },
      ja: {
        title: `中古${brandModel}車を販売しています　| Carro Japan`,
        metaTitle: `中古${brandModel}車を販売しています　| Carro Japan`,
        description: `高品質の中古${brandModel}車を返金保証付で購入いただけます`,
      },
    },
    hk: {
      en: {
        title: `Used ${brandModel} Cars for Sale in Hong Kong | ${companyName}`,
        metaTitle: `Used ${brandModel} Cars for Sale in Hong Kong | ${companyName}`,
        description: `Buy certified, high-quality used ${brandModel} cars in Hong Kong with a money-back guarantee!`,
      },
      'zh-hant-hk': {
        title: `Used ${brandModel} Cars for Sale in Hong Kong | ${companyName}`,
        metaTitle: `Used ${brandModel} Cars for Sale in Hong Kong | ${companyName}`,
        description: `Buy certified, high-quality used ${brandModel} cars in Hong Kong with a money-back guarantee!`,
      },
      'zh-hans-hk': {
        title: `Used ${brandModel} Cars for Sale in Hong Kong | ${companyName}`,
        metaTitle: `Used ${brandModel} Cars for Sale in Hong Kong | ${companyName}`,
        description: `Buy certified, high-quality used ${brandModel} cars in Hong Kong with a money-back guarantee!`,
      },
    },
  };
};
